import { Box, Typography, Drawer, Tooltip, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import DestinationPanel from './DestinationPanel';
import PropTypes from 'prop-types';
import { useThemeContext } from '../Context/ThemeContext';
import { AnimatePresence, motion } from 'framer-motion';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import destinationIcon from './images/destination.png';

const EXPANDED_W = 260;
const COLLAPSED_W = 64;

const RightSideDrawer = forwardRef(
  ({ collapsedBoth, onCollapsedChange }, ref) => {
    const { darkMode } = useThemeContext();

    const [collapsed, setCollapsed] = useState(false);

    const dstRef = useRef(null);

    const goExpandTo = (ref) => {
      setCollapsed(false);
      onCollapsedChange?.(false); 
      setTimeout(() => ref?.current?.scrollIntoView({ behavior: 'smooth' }), 0);
    };

    useImperativeHandle(ref, () => ({
      isRightCollapsed: collapsed,
      toggleRightCollapsed: () => setCollapsed((v) => !v),
    }));

    useEffect(() => {
      if (typeof collapsedBoth === 'boolean') setCollapsed(collapsedBoth);
    }, [collapsedBoth]);

    const toggleBoth = () => {
      const next = !collapsed;
      setCollapsed(next);
      onCollapsedChange?.(next);
    };

    return (
      <Box>
        <Drawer
          anchor="right"
          variant="permanent"
          sx={{
            width: collapsed ? COLLAPSED_W : EXPANDED_W,
            transition: 'width 0.28s ease',
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: collapsed ? COLLAPSED_W : EXPANDED_W,
              transition: 'width 0.28s ease, padding 0.28s ease',
              boxSizing: 'border-box',
              backgroundColor: darkMode ? '#282828' : '#fff',
              color: darkMode ? '#fff' : 'inherit',
              p: collapsed ? 1 : 2,
              borderRadius:'4px',
              borderLeft: `1px solid ${darkMode ? '#555' : '#ccc'}`,
              mt: 12,
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              overflowX: 'hidden',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {/* <IconButton size="small" onClick={() => setCollapsed((v) => !v)}> */}
            <IconButton size="small" onClick={toggleBoth}>
              {collapsed ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>
          </Box>

          {!collapsed && <Box/>}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              ml: 'auto',
              marginY: 0.5,
            }}
          ></Box>

          <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    marginTop: 1,
                    color: darkMode ? '#fff' : 'inherit',
                  }}
                >
                  Components
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence initial={false} mode="popLayout">
            {collapsed ? (
              <motion.div
                key="icons"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 1,
                  }}
                >
                  <Tooltip title="Destination" placement="right">
                    <IconButton onClick={() => goExpandTo(dstRef)}>
                      <img
                        src={destinationIcon}
                        alt="Destination"
                        width={24}
                        height={24}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </motion.div>
            ) : (
              <motion.div
                key="panels"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.25 }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    mt: 2,
                    pb: 10,
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#C5CEEA transparent',

                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#C5CEEA',
                      borderRadius: 8,
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#9BACDD',
                    },
                  }}
                >
                  <div ref={dstRef}>
                    <DestinationPanel />
                  </div>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Drawer>
      </Box>
    );
  },
);

RightSideDrawer.propTypes = {
  workflowId: PropTypes.string,
  clearSourceSelection: PropTypes.number,
  collapsedBoth: PropTypes.bool,
  onCollapsedChange: PropTypes.func,
};

RightSideDrawer.displayName = 'RightSideDrawer';

export default RightSideDrawer;
